import { useEffect, useRef, useState } from "react"

import { UseQueryResult } from "react-query"

export const DASHBOARD_PAGE_SIZE = 20

const getPageCount = (total: number | null, page: number, rows: number) => {
  if (total === null) return rows === DASHBOARD_PAGE_SIZE ? page + 1 : page
  return Math.max(1, Math.ceil(total / DASHBOARD_PAGE_SIZE))
}

interface IDashboardTablePagingOptions {
  query: UseQueryResult<unknown>
  page: number
  shownPage: number
  rowCount: number
  total: number | null
  setPage: (page: number) => void
  // Read out once the result of the admin's own change has loaded.
  resultText: string
}

// Page count, the clamp of a page past the end and the result announcement of a
// paged dashboard table.
const useDashboardTablePaging = ({
  query,
  page,
  shownPage,
  rowCount,
  total,
  setPage,
  resultText,
}: IDashboardTablePagingOptions) => {
  const { data } = query
  const pageCount = getPageCount(total, shownPage, rowCount)

  // Set by the admin's own changes, so first load, the page clamp and
  // background refreshes stay silent.
  const isAnnouncePending = useRef(false)
  const [announcement, setAnnouncement] = useState("")
  // Re-runs the check below when a change leaves the query as it was.
  const [announceRequest, setAnnounceRequest] = useState(0)

  // Emptied first, so the same summary after a sort is read out again.
  const announceNextResult = () => {
    isAnnouncePending.current = true
    setAnnouncement("")
    setAnnounceRequest((request) => request + 1)
  }

  useEffect(() => {
    if (!isAnnouncePending.current || !data) return
    if (query.isPreviousData || query.isFetching) return
    isAnnouncePending.current = false
    setAnnouncement(resultText)
  }, [data, query.isPreviousData, query.isFetching, announceRequest])

  useEffect(() => {
    if (!data || query.isPreviousData || total === null) return
    if (page > pageCount) setPage(pageCount)
  }, [data, query.isPreviousData])

  return { pageCount, announcement, announceNextResult }
}

export default useDashboardTablePaging
